// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { ListItem, FormControlLabel, Typography, Box, Checkbox } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppSelector } from 'Hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { deliverableCost } from 'Utils/deliverables';
import paperClipIcon from 'Assets/paper-clip.png';
import { WideLightTooltip } from 'Components/Tooltip';
import { useSelector } from 'react-redux';
import { getOrders } from 'Features/order/orderSelectors';
import { RootState } from 'Store';
import { selectDependencyNames } from 'Features/bundle/bundleSelectors';
import stylesDef from './styles';

interface DeliverableListItemProps {
  item: IDeliverable;
  onAddOnsClick: (selectedList: { item: IDeliverable, checked: boolean, typeFactor3D?:boolean }[]) => void;
}

const DeliverableListItem = ({ item, onAddOnsClick }: DeliverableListItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const { amtChild, salesNoCredits } = useFlags();
  const { selectedBundle, selectedAdditionals } = useAppSelector(
    (state) => state.bundle,
  );
  const { project, rasterizationSelected } = useAppSelector(
    (state) => state.project,
  );
  const { activeOrder } = useSelector((state: RootState) => getOrders(state));
  const { name: bundleName, bundleId, type } = selectedBundle || {};
  const dependencies = useSelector((state) => selectDependencyNames(state, item));
  const tifUploaded = project.tifFiles?.length;
  const showCredit = !amtChild && !salesNoCredits && type !== 'utilities';

  return (
    <Box sx={((bundleName === '2D' && item.requirements.includes('1')) ||
      (bundleId === 4 && [12, 13, 15].includes(item.deliverableId)) ||
      (bundleId === 5 && item.deliverableId === 11) ||
      (type !== 'utilities' && !item.selectable) ||
      (bundleId === 2 && item.deliverableId === 52 && (!tifUploaded && !rasterizationSelected))) &&
      styles.unavailableDeliverables}
    >
      {bundleName === '2D' && item.requirements.includes('1') && (
        <Typography sx={styles.noDeliverableMessage}>
          Please select the 2D + TOPO bundle to add
          {' '}
          {item.name}
          {' '}
          at checkout
        </Typography>
      )}
      <ListItem sx={styles.listItem}>
        <FormControlLabel
          sx={styles.formControl}
          label={(
            <Box style={styles.layerLabel}>
              {dependencies.length > 0 && (
                <WideLightTooltip
                  placement="bottom"
                  title={(
                    <>
                      This layer requires the selection of
                      {' '}
                      {dependencies.map((dep: any, index: number) => (
                        <React.Fragment key={dep}>
                          <b>{dep}</b>
                          {index < dependencies.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                      . These will be added to your scope for convenience.
                    </>
                  )}
                >
                  <img src={paperClipIcon} alt="tooltip" style={styles.paperClipIcon} />
                </WideLightTooltip>
              )}
              <Typography variant="body1" display="inline" sx={styles.itemName}>{item.name}</Typography>
              {(bundleId === 2 && item.deliverableId === 52) && <Typography variant="body1" display="inline" sx={styles.itemName}> (Rasterization required)</Typography>}
            </Box>
          )}
          control={(
            <Checkbox
              sx={styles.checked}
              disabled={type !== 'utilities' && !item.selectable}
              checked={selectedAdditionals.findIndex((i) => i._id === item._id) !== -1}
              onChange={(e) => onAddOnsClick([{ item, checked: e.target.checked }])}
            />
          )}
        />
        <Typography sx={styles.right} component="span">
          {showCredit && ` ${deliverableCost(item.typeFactor[0], activeOrder.acreage).toFixed(2)} credits`}
        </Typography>
      </ListItem>
    </Box>
  );
};

export default DeliverableListItem;
